import { defineNuxtRouteMiddleware } from '#app/composables/router';
import { QUERY_PARAMS } from '~/constants/auth';
import { useAuth } from '~/stores/auth';
import { useSystemInfo } from '~/stores/system-info';
import { isUnderTest } from '~/utils/env/is-under-test';
import { isGuestOrPublicRoute } from '~/utils/routes';

/**
 * Post-auth onboarding gate. For authenticated users with outstanding
 * onboarding requirements (today: terms acceptance; later: project / role
 * setup) redirect into the corresponding onboarding page.
 *
 * Runs after `2.private.global.ts`, which only probes auth for non-guest /
 * non-public routes — so `loggedIn` is only guaranteed settled on those.
 * Re-call `useAuth()` here to ensure state is resolved before deciding.
 * Skips the onboarding page itself to avoid redirect loops.
 */
export default defineNuxtRouteMiddleware(async (to) => {
	if (isUnderTest()) {
		return;
	}

	if (isGuestOrPublicRoute(to)) {
		return;
	}

	if (to.name === 'onboarding.user') return;

	const systemInfo = useSystemInfo();
	const [{ loggedIn }] = await Promise.all([useAuth(), systemInfo.ensureLoaded()]);

	if (!loggedIn.value) return;
	if (!systemInfo.requireAcceptTerms) return;

	return {
		name: 'onboarding.user',
		query: {
			[QUERY_PARAMS.redirect]: to.fullPath,
		},
	};
});
